import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Traduz a URL de uma tela de cadastro no que ela deve mostrar.
 *
 * Todas as entidades seguem o mesmo desenho de endereço:
 *
 *   /<base>              → lista
 *   /<base>/novo         → formulário de criação
 *   /<base>/:id/editar   → formulário de edição
 *   /<base>/:id/<acao>   → ação nomeada sobre o registro (baixar, entrada…)
 *   /<base>/:id          → visualização
 *
 * Deixar isso num hook único evita que cada tela invente seu próprio
 * jeito de ler a URL — e garante que criar, editar e visualizar tenham
 * endereço em todas elas, não só nas que alguém lembrou de fazer.
 */

export type ModoRota =
  | { modo: 'lista' }
  | { modo: 'novo' }
  | { modo: 'editar'; id: string }
  | { modo: 'detalhe'; id: string }
  | { modo: 'acao'; id: string; acao: string };

export type RotaEntidade = ModoRota & {
  /** Volta para a listagem (fecha o formulário). */
  fechar: () => void;
  /** Abre uma sub-rota qualquer da entidade. */
  abrir: (caminhoRelativo: string) => void;
};

export function useRotaEntidade(base: string): RotaEntidade {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const estado = useMemo<ModoRota>(() => {
    if (!pathname.startsWith(base)) return { modo: 'lista' };
    const resto = pathname.slice(base.length).replace(/^\/+/, '').replace(/\/+$/, '');
    if (resto === '') return { modo: 'lista' };

    const partes = resto.split('/');
    if (partes[0] === 'novo' || partes[0] === 'nova') return { modo: 'novo' };

    const [id, acao] = partes;
    if (!acao) return { modo: 'detalhe', id };
    if (acao === 'editar') return { modo: 'editar', id };
    return { modo: 'acao', id, acao };
  }, [pathname, base]);

  const fechar = useCallback(() => navigate(base), [navigate, base]);
  const abrir = useCallback(
    (caminhoRelativo: string) =>
      navigate(`${base}/${caminhoRelativo}`.replace(/\/+$/, '')),
    [navigate, base],
  );

  return { ...estado, fechar, abrir };
}
